import * as reactiveUtils from '@arcgis/core/core/reactiveUtils';
import * as webMercatorUtils from '@arcgis/core/geometry/support/webMercatorUtils';
import type { MapFeatureLinkCaptureRegion } from '../models/map-feature-link';
import { captureRegionFromView, captureScreenshotSize } from './design-context';

/** Spec 2026-09-27 §2.2: wait for tiles / vector layers to finish drawing, but never more than this. */
export const MAP_SETTLE_TIMEOUT_MS = 2000;

/** Resolves once the view has stopped updating (basemap tiles, reference layers, graphics), or after the timeout. */
export async function waitForMapSettle(view: __esri.MapView, timeoutMs = MAP_SETTLE_TIMEOUT_MS): Promise<void> {
  if (!view.updating) return;
  let timer: ReturnType<typeof setTimeout>;
  const timeout = new Promise<void>(resolve => { timer = setTimeout(resolve, timeoutMs); });
  try {
    await Promise.race([reactiveUtils.whenOnce(() => !view.updating).then(() => undefined), timeout]);
  } finally {
    clearTimeout(timer);
  }
}

/** A picker capture: the PNG and the map region it pictures (spec addendum §11; null when it could not be read). */
export interface MapViewCapture {
  dataUrl: string;
  region: MapFeatureLinkCaptureRegion | null;
}

/**
 * The map as drawn: basemap, design layers and the pin graphic. `takeScreenshot` renders the
 * map canvas only, so the dialog's panel, hint and buttons are never in the picture (the same
 * call the sites map uses for its summary export, sites-location-map.component.ts ~6920).
 * The extent, zoom and rotation are read synchronously right before the screenshot is requested, so they describe the
 * frame it renders (the whole view, at any pixel size); the pixel size is the image's own. A region that cannot be
 * read never fails the capture.
 */
export async function captureMapView(view: __esri.MapView, center?: __esri.Point | null): Promise<MapViewCapture> {
  // The pin at the picture's centre (as on mobile, where the pin is the map centre): the location slot's cover crop
  // is centred on the image, so it is centred on the pin. The camera moves; the pin does not.
  if (center) await view.goTo({ center }, { animate: false }).catch(() => undefined);
  await waitForMapSettle(view);
  const state = readViewState(view);
  // Twice the view's CSS size: ArcGIS renders the frame at that size rather than upscaling the canvas.
  const size = captureScreenshotSize({ width: view.width, height: view.height });
  const shot = await view.takeScreenshot({ format: 'png', ...(size || {}) });
  if (!shot?.dataUrl) throw new Error('takeScreenshot returned no image');
  let region: MapFeatureLinkCaptureRegion | null = null;
  if (state && shot.data) region = captureRegionFromView({ ...state, width: shot.data.width, height: shot.data.height });
  return { dataUrl: shot.dataUrl, region };
}

function readViewState(view: __esri.MapView): { xmin: number; ymin: number; xmax: number; ymax: number; zoom: number; rotation: number } | null {
  try {
    const extent = view.extent;
    if (!extent) return null;
    const sr = extent.spatialReference;
    const geographic = sr?.isWebMercator ? webMercatorUtils.webMercatorToGeographic(extent, false) as __esri.Extent
      : sr?.isWGS84 ? extent : null;
    if (!geographic) return null;
    return { xmin: geographic.xmin, ymin: geographic.ymin, xmax: geographic.xmax, ymax: geographic.ymax, zoom: view.zoom, rotation: view.rotation };
  } catch {
    return null;
  }
}
